import api from './api';

// Cache presigned GET URLs to avoid re-requesting on every render
// TTL: 50 minutes (presigned URLs expire after 60 min)
const urlCache = new Map<string, { url: string; expiry: number }>();
const CACHE_TTL = 50 * 60 * 1000; // 50 minutes in ms

export const mediaService = {
  async presignUpload(
    filename: string,
    contentType: string,
    fileSize: number,
  ): Promise<{ uploadUrl: string; key: string }> {
    const response = await api.post('/media/presign', {
      filename,
      contentType,
      fileSize,
    });
    return response.data;
  },

  async getSignedUrl(key: string): Promise<{ url: string }> {
    const response = await api.get('/media/signed-url', {
      params: { key },
    });
    return response.data;
  },

  uploadToS3(
    uploadUrl: string,
    file: File,
    onProgress?: (percent: number) => void,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      };

      xhr.onerror = () => reject(new Error('Upload failed'));
      xhr.send(file);
    });
  },

  async resolveMediaUrl(content: string): Promise<string> {
    if (!content) return '';

    // External URLs: use directly
    if (content.startsWith('http://') || content.startsWith('https://')) {
      return content;
    }

    // S3 key: check cache first
    const cached = urlCache.get(content);
    if (cached && Date.now() < cached.expiry) {
      return cached.url;
    }

    // Fetch presigned GET URL
    const { url } = await mediaService.getSignedUrl(content);
    urlCache.set(content, { url, expiry: Date.now() + CACHE_TTL });
    return url;
  },
};
